interface ParsedVersion {
  major: number;
  minor: number;
  patch: number;
  valid: boolean;
}

export function parseVersion(version: string): ParsedVersion {
  if (!version || typeof version !== 'string') {
    return { major: 0, minor: 0, patch: 0, valid: false };
  }

  // Remove any prefix like 'v' and suffixes like '-beta', '-alpha'
  const cleanVersion = version.replace(/^v/, '').split('-')[0];

  // Split by dots
  const parts = cleanVersion.split('.');

  if (parts.length < 2 || parts.length > 3) {
    return { major: 0, minor: 0, patch: 0, valid: false };
  }

  const major = parseInt(parts[0], 10);
  const minor = parseInt(parts[1], 10);
  const patch = parts.length === 3 ? parseInt(parts[2], 10) : 0;

  // Check if all parts are valid numbers
  if (isNaN(major) || isNaN(minor) || isNaN(patch)) {
    return { major: 0, minor: 0, patch: 0, valid: false };
  }

  return { major, minor, patch, valid: true };
}

export function isVersionLower(currentVersion: string, targetVersion: string): boolean {
  const current = parseVersion(currentVersion);
  const target = parseVersion(targetVersion);

  // If either version is invalid, return false (don't force update)
  if (!current.valid || !target.valid) {
    console.warn('[VERSION-COMPARATOR] Invalid version format:', {
      current: currentVersion,
      target: targetVersion,
    });
    return false;
  }

  // Compare major version
  if (current.major < target.major) {
    return true;
  }
  if (current.major > target.major) {
    return false;
  }

  // Major versions are equal, compare minor
  if (current.minor < target.minor) {
    return true;
  }
  if (current.minor > target.minor) {
    return false;
  }

  // Major and minor are equal, compare patch
  return current.patch < target.patch;
}

export function compareVersions(
  version1: string,
  version2: string
): -1 | 0 | 1 {
  const v1 = parseVersion(version1);
  const v2 = parseVersion(version2);

  if (!v1.valid || !v2.valid) {
    return 0;
  }

  // Compare major
  if (v1.major < v2.major) return -1;
  if (v1.major > v2.major) return 1;

  // Compare minor
  if (v1.minor < v2.minor) return -1;
  if (v1.minor > v2.minor) return 1;

  // Compare patch
  if (v1.patch < v2.patch) return -1;
  if (v1.patch > v2.patch) return 1;

  return 0;
}

export function isVersionEqual(version1: string, version2: string): boolean {
  return compareVersions(version1, version2) === 0;
}

export function isVersionGreater(
  version1: string,
  version2: string
): boolean {
  return compareVersions(version1, version2) === 1;
}

export function validateVersionFormat(version: string): boolean {
  return parseVersion(version).valid;
}
